import React, { useEffect } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';

import { capitalizeString, DictionaryDetail, validationsMessages } from 'lib';
import { ColorPicker } from 'components';
import { PairedInputSelectionWidget } from '../../../../components/paired-input-selection-widget/PairedInputSelectionWidget';
import { AttributeAssociationItem } from '../../../edge-definitions/edge-definition-helper';
import {
    glossaryMetadataFormStyles,
    glossaryValidationMessages,
} from '../../../glossary-metadata/glossary-metadata-helper';
import {
    dictionaryManagerDetailStyles,
    undefinedAttributeOptions,
    colorDataDictionary,
} from './dictionaryManagerDetails-helpers';
import { Divider, Radio, RadioGroup, TextField } from '@biq/apex-ui';
import { LabelAndValueComponent } from 'pages/wallets-and-groups/components/create-or-edit-user-wallet/user-wallet-from-helper';

export interface DictionaryManagerDetailsProps {
    details?: DictionaryDetail;
    setDetails: Function;
    isEdit: boolean;
    isPredicted: boolean;
    control: any;
    setValue: any;
    watch: any;
    errors: any;
    setIsChangesUnsaved?: React.Dispatch<React.SetStateAction<boolean>>;
    propertiesAssociationList: AttributeAssociationItem[];
    handlePropertyListChanged: (newList: AttributeAssociationItem[]) => void;
}

export const DictionaryManagerDetails = (
    props: DictionaryManagerDetailsProps
): React.JSX.Element => {
    const {
        details,
        setDetails,
        isEdit,
        isPredicted,
        control,
        setValue,
        watch,
        errors,
        setIsChangesUnsaved,
        propertiesAssociationList,
        handlePropertyListChanged,
    } = props;
    const layoutMetadata = details?.layoutMetadata ?? null;

    useEffect(() => {
        if (isEdit || isPredicted) {
            setValue('name', details?.name ?? '');
            setValue('description', details?.description ?? '');
            setValue('groupName', details?.groupName ?? '');
            setValue(
                'undefinedAttributesAction',
                details?.undefinedAttributesAction ?? ''
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [details]);

    const onPropertyListChanged = (
        newList: AttributeAssociationItem[]
    ): void => {
        setIsChangesUnsaved?.(true);
        setDetails({ ...details, properties: newList });
        handlePropertyListChanged(newList);
    };

    const handleCustomNodeColorChange = (
        color: React.ChangeEvent<HTMLInputElement>
    ): void => {
        if (color) {
            setIsChangesUnsaved?.(true);
            setDetails({ ...details, layoutMetadata: { color } });
        }
    };

    return (
        <Grid sx={dictionaryManagerDetailStyles.containerStyles}>
            {isEdit ? (
                <LabelAndValueComponent label='Name' value={watch('name')} />
            ) : (
                <Box sx={dictionaryManagerDetailStyles.fieldGroup}>
                    <Controller
                        name='name'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                label='Name'
                                required
                                onChange={(value): void => {
                                    field.onChange(value);
                                    setDetails({
                                        ...details,
                                        name: value.target.value,
                                    });
                                }}
                                error={!!errors?.name}
                                errorText={
                                    errors.name ? errors?.name?.message : ''
                                }
                            />
                        )}
                    />
                </Box>
            )}
            <Box sx={dictionaryManagerDetailStyles.fieldGroup}>
                <Controller
                    name='description'
                    control={control}
                    render={({ field }): React.JSX.Element => (
                        <TextField
                            {...field}
                            variant='outlined'
                            label='Description'
                            minRows={1}
                            maxRows={12}
                            multiline
                            onChange={(value): void => {
                                setIsChangesUnsaved?.(true);
                                field.onChange(value);
                                setDetails({
                                    ...details,
                                    description: value.target.value,
                                });
                            }}
                            onBlur={(value): void => {
                                const trimmedValue = value.target.value.trim();
                                field.onChange(trimmedValue);
                                setDetails({
                                    ...details,
                                    description: trimmedValue,
                                });
                            }}
                            error={!!errors.description}
                            errorText={
                                errors.description
                                    ? errors.description.message
                                    : ''
                            }
                        />
                    )}
                />
            </Box>
            <Box sx={dictionaryManagerDetailStyles.fieldGroup}>
                <Controller
                    name='groupName'
                    control={control}
                    render={({ field }): React.JSX.Element => (
                        <TextField
                            {...field}
                            variant='outlined'
                            label='Dictionary Grouping'
                            onChange={(value): void => {
                                setIsChangesUnsaved?.(true);
                                field.onChange(value);
                                setDetails({
                                    ...details,
                                    groupName: value.target.value,
                                });
                            }}
                            error={!!errors?.groupName}
                            errorText={
                                errors.groupName
                                    ? errors?.groupName?.message
                                    : ''
                            }
                        />
                    )}
                />
            </Box>
            <Box sx={dictionaryManagerDetailStyles.fieldGroup}>
                <Controller
                    name='undefinedAttributesAction'
                    control={control}
                    render={({ field }): React.JSX.Element => (
                        <RadioGroup
                            {...field}
                            label='Undefined Attributes Action'
                            onChange={(value): void => {
                                setIsChangesUnsaved?.(true);
                                field.onChange(value);
                                setDetails({
                                    ...details,
                                    undefinedAttributesAction:
                                        value.target.value,
                                });
                            }}
                            error={!!errors.undefinedAttributesAction}
                            errorText={
                                errors.undefinedAttributesAction
                                    ? errors.undefinedAttributesAction.message
                                    : ''
                            }
                        >
                            {React.Children.toArray(
                                undefinedAttributeOptions?.map(
                                    (val: string) => (
                                        <Radio
                                            label={capitalizeString(val)}
                                            value={val}
                                            checked={
                                                watch(
                                                    'undefinedAttributesAction'
                                                ) === val
                                            }
                                        />
                                    )
                                )
                            )}
                        </RadioGroup>
                    )}
                />
                <Divider sx={dictionaryManagerDetailStyles.divider} />
                <Box sx={dictionaryManagerDetailStyles.propertiesFieldGroup}>
                    <PairedInputSelectionWidget
                        attributeAssociationList={propertiesAssociationList}
                        handleAttributeAssociationsChanged={
                            onPropertyListChanged
                        }
                        title={'Properties'}
                        addButtonLabel={'Property'}
                        duplicateErrorMessage={
                            glossaryValidationMessages.duplicatedOptions
                        }
                        emptyErrorMessage={validationsMessages.required}
                        leftSideInputLabel={'Name'}
                        rightSideInputLabel={'Value'}
                        checkForDuplicates={true}
                        leftSideInputStyles={
                            glossaryMetadataFormStyles.propertiesEditInputContainer
                        }
                        rightSideInputStyles={
                            glossaryMetadataFormStyles.propertiesEditInputContainer
                        }
                        customInputSelectionStyles={
                            glossaryMetadataFormStyles.propertiesInputMainContainer
                        }
                        displayEmptyState={false}
                        addButtonContainerStyles={
                            glossaryMetadataFormStyles.addPropertiesButtonContainer
                        }
                        addButtonStyles={
                            glossaryMetadataFormStyles.addPropertiesButton
                        }
                        titleContainerStyles={
                            glossaryMetadataFormStyles.titleContainer
                        }
                        widgetMainContainerStyles={
                            glossaryMetadataFormStyles.propertiesContainer
                        }
                        deleteButtonContainerStyles={
                            glossaryMetadataFormStyles.deleteButtonContainer
                        }
                        deleteButtonStyles={
                            glossaryMetadataFormStyles.deleteButton
                        }
                    />
                </Box>
                <Divider sx={dictionaryManagerDetailStyles.divider} />
                <Box sx={dictionaryManagerDetailStyles.nodeColorWrapper}>
                    <Typography
                        sx={dictionaryManagerDetailStyles.nodeColorLabelStyles}
                    >
                        Node Color
                    </Typography>
                    <ColorPicker
                        presetColors={colorDataDictionary}
                        onChange={handleCustomNodeColorChange}
                        value={layoutMetadata?.color}
                    />
                </Box>
            </Box>
        </Grid>
    );
};
